<script setup>
// Templates de e-mail: lista + editor drag-drop profissional (GrapesEmailEditor).
// O editor exporta HTML email-safe + o design (pra re-editar). Salva no CRUD
// MarketingEmailTemplate.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import API from 'dashboard/api/marketingEmailTemplates';
import GrapesEmailEditor from './GrapesEmailEditor.vue';

const { t } = useI18n();

const templates = ref([]);
const loading = ref(false);
const saving = ref(false);
const editing = ref(null); // { id?, name, subject, design }
const grapesRef = ref(null);

// Variáveis do CONTATO + do REMETENTE (a empresa do cliente, no rodapé).
const VARIABLES = [
  '{{nome}}',
  '{{empresa}}',
  '{{email}}',
  '{{remetente}}',
  '{{endereco}}',
];

function tp(key) {
  return t(`EMAIL_TEMPLATES.${key}`);
}

async function fetchTemplates() {
  loading.value = true;
  try {
    const { data } = await API.get();
    templates.value = data;
  } catch {
    useAlert(tp('LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

function newTemplate() {
  editing.value = { name: '', subject: '', design: {} };
}

function editTemplate(tpl) {
  editing.value = {
    id: tpl.id,
    name: tpl.name,
    subject: tpl.subject || '',
    design: tpl.design || {},
  };
}

function duplicateTemplate(tpl) {
  editing.value = {
    name: `${tpl.name} (cópia)`,
    subject: tpl.subject || '',
    design: tpl.design || {},
  };
}

function cancelEdit() {
  editing.value = null;
}

async function saveTemplate() {
  const tpl = editing.value;
  if (!tpl.name.trim()) {
    useAlert(tp('NAME_REQUIRED'));
    return;
  }
  const { html, design } = grapesRef.value.getContent();
  saving.value = true;
  const payload = {
    marketing_email_template: {
      name: tpl.name.trim(),
      subject: tpl.subject,
      body_html: html,
      design,
    },
  };
  try {
    if (tpl.id) {
      await API.update(tpl.id, payload);
    } else {
      await API.create(payload);
    }
    useAlert(tp('SAVED'));
    editing.value = null;
    await fetchTemplates();
  } catch (error) {
    useAlert(error?.response?.data?.message || tp('SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
}

async function deleteTemplate(tpl) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(tp('DELETE_CONFIRM'))) return;
  try {
    await API.delete(tpl.id);
    await fetchTemplates();
  } catch {
    useAlert(tp('SAVE_ERROR'));
  }
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString() : '—';
}

onMounted(fetchTemplates);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- LISTA -->
    <template v-if="!editing">
      <div class="flex flex-col w-full h-full p-6 overflow-auto">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ tp('HEADER') }}
            </h1>
            <p class="mt-1 text-sm text-n-slate-11">{{ tp('DESCRIPTION') }}</p>
          </div>
          <button
            class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand-dark"
            @click="newTemplate"
          >
            <span class="i-lucide-plus" />
            {{ tp('NEW') }}
          </button>
        </div>

        <div v-if="loading" class="py-10 text-sm text-center text-n-slate-11">
          {{ tp('LOADING') }}
        </div>
        <div
          v-else-if="!templates.length"
          class="py-10 text-sm text-center rounded-lg text-n-slate-11 bg-n-solid-2"
        >
          {{ tp('EMPTY') }}
        </div>
        <table v-else class="w-full text-sm divide-y divide-n-weak">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2 pr-4 font-medium">{{ tp('COL_NAME') }}</th>
              <th class="py-2 pr-4 font-medium">{{ tp('COL_SUBJECT') }}</th>
              <th class="py-2 pr-4 font-medium">{{ tp('COL_UPDATED') }}</th>
              <th class="py-2" />
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-12">
            <tr v-for="tpl in templates" :key="tpl.id">
              <td class="py-3 pr-4 font-medium">{{ tpl.name }}</td>
              <td class="py-3 pr-4 text-n-slate-11">
                {{ tpl.subject || '—' }}
              </td>
              <td class="py-3 pr-4 text-n-slate-11">
                {{ formatDate(tpl.updated_at) }}
              </td>
              <td class="py-3 text-right whitespace-nowrap">
                <button
                  class="px-2 text-n-slate-11 hover:text-n-brand"
                  :title="tp('EDIT')"
                  @click="editTemplate(tpl)"
                >
                  <span class="i-lucide-pencil" />
                </button>
                <button
                  class="px-2 text-n-slate-11 hover:text-n-brand"
                  :title="tp('DUPLICATE')"
                  @click="duplicateTemplate(tpl)"
                >
                  <span class="i-lucide-copy" />
                </button>
                <button
                  class="px-2 text-n-slate-11 hover:text-n-ruby-9"
                  :title="tp('DELETE')"
                  @click="deleteTemplate(tpl)"
                >
                  <span class="i-lucide-trash-2" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- EDITOR -->
    <template v-else>
      <div
        class="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-n-weak bg-n-solid-1"
      >
        <button
          class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="cancelEdit"
        >
          <span class="i-lucide-arrow-left" />
          {{ tp('BACK') }}
        </button>
        <input
          v-model="editing.name"
          type="text"
          :placeholder="tp('NAME')"
          class="w-48 px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
        <input
          v-model="editing.subject"
          type="text"
          :placeholder="tp('SUBJECT')"
          class="flex-1 min-w-[12rem] px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
        <span class="text-xs text-n-slate-10">
          {{ tp('VARIABLES_HINT') }} {{ VARIABLES.join(' · ') }}
        </span>
        <button
          class="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand-dark disabled:opacity-50"
          :disabled="saving"
          @click="saveTemplate"
        >
          <span v-if="saving" class="i-lucide-loader-2 animate-spin" />
          {{ tp('SAVE') }}
        </button>
      </div>

      <div class="flex-1 min-h-0">
        <GrapesEmailEditor
          :key="editing.id || 'new'"
          ref="grapesRef"
          :design="editing.design"
        />
      </div>
    </template>
  </div>
</template>
